// src/features/marketplace/pages/Messages.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../../../firebase';
import { 
  collection, query, where, orderBy, onSnapshot, 
  addDoc, updateDoc, doc, serverTimestamp, setDoc 
} from 'firebase/firestore';
import { ArrowLeft, Send, MessageCircle } from 'lucide-react';

export const Messages = ({ showToast }) => {
  const { id: conversationId } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!auth.currentUser) {
      navigate('/login');
      return;
    }

    const q = query(
      collection(db, "marketplace_conversations"),
      where("participants", "array-contains", auth.currentUser.uid),
      orderBy("lastMessageAt", "desc")
    );

    return onSnapshot(q, (snap) => {
      const convos = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setConversations(convos);

      if (conversationId) {
        const found = convos.find(c => c.id === conversationId);
        if (found) setSelectedConversation(found);
      } else if (convos.length > 0 && !selectedConversation) {
        setSelectedConversation(convos[0]);
      }
    });
  }, [conversationId, navigate]);

  useEffect(() => {
    if (!selectedConversation) return;

    const q = query(
      collection(db, "marketplace_messages"),
      where("conversationId", "==", selectedConversation.id),
      orderBy("createdAt", "asc")
    );

    return onSnapshot(q, (snap) => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      
      if (auth.currentUser) {
        const unreadCount = selectedConversation.unreadCount || {};
        if (unreadCount[auth.currentUser.uid] > 0) {
          updateDoc(doc(db, "marketplace_conversations", selectedConversation.id), {
            [`unreadCount.${auth.currentUser.uid}`]: 0
          });
        }
      }
    });
  }, [selectedConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !selectedConversation) return;

    const otherUserId = selectedConversation.participants.find(p => p !== auth.currentUser.uid);

    try {
      const messageId = `msg_${Date.now()}`;
      await setDoc(doc(db, "marketplace_messages", messageId), {
        conversationId: selectedConversation.id,
        fromUser: auth.currentUser.uid,
        toUser: otherUserId,
        listingId: selectedConversation.listingId,
        content: messageText,
        createdAt: serverTimestamp(),
        read: false
      });

      await updateDoc(doc(db, "marketplace_conversations", selectedConversation.id), {
        lastMessage: messageText,
        lastMessageAt: serverTimestamp(),
        [`unreadCount.${otherUserId}`]: (selectedConversation.unreadCount?.[otherUserId] || 0) + 1
      });

      setMessageText('');
    } catch (error) {
      console.error("Błąd wysyłania:", error);
      showToast("Błąd wysyłania wiadomości", "error");
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="h-screen flex bg-gray-50 overflow-hidden max-w-full">
      {/* Conversations List */}
      <div className={`${selectedConversation ? 'hidden md:block' : 'block'} w-full md:w-80 bg-white border-r border-gray-200 flex flex-col max-w-full`}>
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Wiadomości</h2>
        </div>

        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-8 text-center">
              <MessageCircle className="mx-auto mb-4 text-gray-300" size={48}/>
              <p className="text-sm text-gray-500">Brak wiadomości</p>
            </div>
          ) : (
            conversations.map(conv => {
              const unread = conv.unreadCount?.[auth.currentUser.uid] || 0;
              return (
                <button
                  key={conv.id}
                  onClick={() => setSelectedConversation(conv)}
                  className={`w-full p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors text-left ${
                    selectedConversation?.id === conv.id ? 'bg-emerald-50' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    {conv.listingImage && (
                      <img src={conv.listingImage} className="w-12 h-12 rounded-lg object-cover" alt=""/>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-gray-900 truncate">
                        {conv.listingTitle}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {conv.lastMessage}
                      </p>
                    </div>
                    {unread > 0 && (
                      <div className="shrink-0 w-5 h-5 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                        {unread}
                      </div>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Chat Area */}
      {selectedConversation ? (
        <div className="flex-1 flex flex-col h-full">
          {/* Chat Header */}
          <div className="p-4 bg-white border-b border-gray-200 flex items-center gap-3 shrink-0">
            <button
              onClick={() => setSelectedConversation(null)}
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft size={20}/>
            </button>
            {selectedConversation.listingImage && (
              <img src={selectedConversation.listingImage} className="w-10 h-10 rounded-lg object-cover" alt=""/>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-gray-900 truncate">{selectedConversation.listingTitle}</h3>
              <button
                onClick={() => navigate(`/market/car/${selectedConversation.listingId}`)}
                className="text-xs text-emerald-600 hover:text-emerald-700"
              >
                Zobacz ogłoszenie →
              </button>
            </div>
          </div>

          {/* Messages - ✅ MOBILE FIX */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 max-w-full" style={{ paddingBottom: '90px' }}>
            {messages.length === 0 && (
              <div className="text-center py-8 px-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 max-w-md mx-auto">
                  <p className="text-sm font-semibold text-blue-900 mb-2">💬 Wiadomości są moderowane</p>
                  <p className="text-xs text-blue-700">
                    Bądź uprzejmy i profesjonalny. Oszustwa, spam i wulgaryzmy są zabronione.
                  </p>
                </div>
              </div>
            )}
            {messages.map(msg => {
              const isMine = msg.fromUser === auth.currentUser.uid;
              return (
                <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'} max-w-full`}>
                  <div className={`max-w-[75%] md:max-w-md ${
                    msg.isOffer 
                      ? 'bg-gradient-to-r from-emerald-100 to-teal-100 border border-emerald-300' 
                      : isMine 
                        ? 'bg-emerald-600 text-white' 
                        : 'bg-gray-200 text-gray-900'
                  } rounded-2xl px-4 py-2 break-words`}>
                    {msg.isOffer && (
                      <p className="text-xs font-semibold text-emerald-700 mb-1">💰 Oferta cenowa</p>
                    )}
                    <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                    <p className={`text-xs mt-1 ${isMine ? 'text-emerald-200' : 'text-gray-500'}`}>
                      {formatTime(msg.createdAt)}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Input - ✅ MOBILE FIX (NAD navbar) */}
          <form 
            onSubmit={handleSendMessage} 
            className="p-4 bg-white border-t border-gray-200 shrink-0 fixed md:relative bottom-0 left-0 right-0 z-[100] shadow-2xl md:shadow-none max-w-full"
          >
            <div className="flex gap-2 w-full">
              <input
                type="text"
                placeholder="Napisz wiadomość..."
                className="flex-1 px-4 py-3 bg-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500 text-sm min-w-0"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
              />
              <button
                type="submit"
                disabled={!messageText.trim()}
                className="px-4 md:px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold hover:from-emerald-700 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shrink-0"
              >
                <Send size={20}/>
                <span className="hidden md:inline">Wyślij</span>
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="hidden md:flex flex-1 items-center justify-center text-gray-400">
          <div className="text-center">
            <MessageCircle className="mx-auto mb-4" size={64}/>
            <p className="font-semibold">Wybierz konwersację</p>
          </div>
        </div>
      )}
    </div>
  );
};